import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TradieCard from '@/components/TradieCard';
import { getTradies, getRegions, getCategories } from '@/lib/api';

const DirectoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tradies, setTradies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(searchParams.get('region') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [regionsData, categoriesData] = await Promise.all([
          getRegions(),
          getCategories()
        ]);
        setRegions(regionsData);
        setCategories(categoriesData);
      } catch (err) {
        console.error('Failed to load filters:', err);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    const loadTradies = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTradies(selectedRegion || null, selectedCategory || null);
        setTradies(data);
      } catch (err) {
        setError('Failed to load tradies. Please try again.');
        console.error('Failed to load tradies:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTradies();
  }, [selectedRegion, selectedCategory]);

  const handleRegionChange = (value) => {
    const newRegion = value === 'all' ? '' : value;
    setSelectedRegion(newRegion);
    updateSearchParams(newRegion, selectedCategory);
  };

  const handleCategoryChange = (value) => {
    const newCategory = value === 'all' ? '' : value;
    setSelectedCategory(newCategory);
    updateSearchParams(selectedRegion, newCategory);
  };

  const updateSearchParams = (region, category) => {
    const params = new URLSearchParams();
    if (region) params.set('region', region);
    if (category) params.set('category', category);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSelectedRegion('');
    setSelectedCategory('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-background" data-testid="directory-page">
      {/* Header */}
      <section className="bg-white border-b border-stone-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
            Find Tradies
          </h1>
          <p className="text-muted-foreground">
            Browse verified professionals across New Zealand
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-stone-200 py-4 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 flex-1">
              <Select value={selectedRegion || 'all'} onValueChange={handleRegionChange}>
                <SelectTrigger className="w-[180px]" data-testid="filter-region">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map((region) => (
                    <SelectItem key={region} value={region}>
                      {region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[200px]" data-testid="filter-category">
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {(selectedRegion || selectedCategory) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  data-testid="clear-filters"
                >
                  Clear filters
                </Button>
              )}
            </div>
            
            <div className="text-sm text-muted-foreground">
              {tradies.length} {tradies.length === 1 ? 'tradie' : 'tradies'} found
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle className="w-12 h-12 text-destructive mb-4" />
              <p className="text-lg font-medium mb-2">Something went wrong</p>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          ) : tradies.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No tradies found</p>
              <p className="text-muted-foreground mb-4">
                {selectedRegion || selectedCategory 
                  ? 'Try adjusting your filters to see more results'
                  : 'No tradies are currently listed. Check back soon!'}
              </p>
              {(selectedRegion || selectedCategory) && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="tradies-grid">
              {tradies.map((tradie) => (
                <TradieCard key={tradie.id} tradie={tradie} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DirectoryPage;
